<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { defaultSizeOption, findMatchingSizeOption } from '../../utils/sizeMatch'
import type { EditorCategoryTreeResponse } from '../../services/editorCategoryApi'
import { fetchEditorCatalogBootstrap, type EditorCatalogBootstrapResponse } from '../../services/editorCatalogApi'
import { resolvePrintboxFamilyId } from '../../utils/printboxCategory'
import { usePriceCalculator } from '../../composables/usePriceCalculator'
import { useEditorCatalogFlow } from '../../composables/useEditorCatalogFlow'
import { compareSizeOptions } from '../../utils/sizeSorting'
import { normalizeTags } from '../../utils/tags'

const props = defineProps<{
  files: any[]
  initialCategoryId?: number | null
  initialProductId?: number | null
  currentEditorType?: string | null
  uploadProgress?: number
  uploadDone?: number
  uploadTotal?: number
  /** Bereits im Projekt verwendete Tags (Vorschläge) */
  knownTags?: string[]
  /** Vorbelegung (zuletzt verwendete Tags) */
  initialTags?: string[]
}>()

const emit = defineEmits<{
  apply: [payload: {
    productId: number
    productTitle?: string
    categoryId: number
    categoryTitle?: string
    categoryThumbnail?: string
    sizeVariantId?: string
    sizeTitle?: string
    sizeWidthMm?: number
    sizeHeightMm?: number
    quantity: number
    grayscale: boolean
    options: Array<{ groupId: string; variantId: string; value: string }>
    allGroups?: any[]
    tags: string[]
  }]
  cancel: []
}>()

/* ── Tags / Alben ────────────────────────────────────── */
const tagsInput = ref(Array.isArray(props.initialTags) ? props.initialTags.join(', ') : '')
const parsedTags = computed(() => normalizeTags(tagsInput.value))
const tagSuggestions = computed(() => (props.knownTags ?? []).filter((t) => !parsedTags.value.some((p) => p.toLowerCase() === t.toLowerCase())))
function addSuggestedTag(tag: string) {
  tagsInput.value = [...parsedTags.value, tag].join(', ')
}

const selectedRootId = ref<number | null>(null)
const selectedSubId  = ref<number | null>(null)
const catNavRef = ref<HTMLElement | null>(null)
const selectedProdId = ref<number | null>(null)
const selectedSizeId = ref<string | null>(null)
const selectedOptions = ref<Map<string, { variantId: string; value: string }>>(new Map())
const customWidthCm = ref<number | null>(null)
const customHeightCm = ref<number | null>(null)
const customSizeEnabled = ref(false)
const categoryTree = ref<EditorCategoryTreeResponse | null>(null)
const catalogBootstrap = ref<EditorCatalogBootstrapResponse | null>(null)
const quantity = ref(1)
const grayscale = ref(false)
const lastSizeTitleBeforeProductSwitch = ref<string | null>(null)

async function loadCategoryTree() {
  const payload = await fetchEditorCatalogBootstrap()
  catalogBootstrap.value = payload
  categoryTree.value = payload.tree
}

function isRegularEditorCategory(category: any): boolean {
  if (!category || category?.showInEditor !== true) return false
  return resolvePrintboxFamilyId(category) === null
}

onMounted(() => {
  loadCategoryTree().catch(() => {})
})

const allRootCats = computed(() =>
  (Array.isArray(categoryTree.value?.roots) ? categoryTree.value!.roots : []).filter((c: any) => isRegularEditorCategory(c))
)
const filteredCats = computed(() => {
  const et = props.currentEditorType
  if (!et) return allRootCats.value
  return allRootCats.value.filter((c: any) => !c.editorType || c.editorType === et)
})

/* ── Kategorie-Vorauswahl: aus dem Produkt (bevorzugt) oder initialCategoryId ── */
// Kommt der Editor per Link mit ?productId=… , wählen wir die spezifischste
// Unterkategorie des Produkts. Achtung: product.categories[0] ist oft die
// Root-Kategorie (ohne Parent) – die reicht nicht, weil dann nur die Root ohne
// Sub selektiert würde. Deshalb bevorzugen wir eine Unterkategorie (mit Parent),
// in der das Produkt tatsächlich gelistet ist.
const effectiveInitialCategoryId = computed<number | null>(() => {
  const items = Array.isArray(categoryTree.value?.items) ? categoryTree.value!.items : []
  const byId = new Map<number, any>(items.map((i: any) => [Number(i?._id), i]))

  const pid = Number(props.initialProductId || 0)
  if (pid > 0) {
    const prod = catalogBootstrap.value?.products?.[String(pid)]
    const cats = Array.isArray((prod as any)?.categories) ? (prod as any).categories : []
    const pbc = catalogBootstrap.value?.productsByCategory ?? {}
    const candidateIds = cats
      .map((c: any) => Number(c?._id))
      .filter((id: number) => id > 0 && byId.has(id))
    // 1. Unterkategorie, die dieses Produkt listet (spezifischste Zuordnung)
    const subListing = candidateIds.find((id: number) =>
      Number(byId.get(id)?.parentId ?? 0) > 0 && (pbc[String(id)] ?? []).includes(pid))
    if (subListing) return subListing
    // 2. Irgendeine Unterkategorie des Produkts
    const anySub = candidateIds.find((id: number) => Number(byId.get(id)?.parentId ?? 0) > 0)
    if (anySub) return anySub
    // 3. Sonst die erste Kategorie des Produkts
    if (candidateIds.length) return candidateIds[0]
  }

  const direct = Number(props.initialCategoryId || 0)
  return direct > 0 ? direct : null
})

watch([categoryTree, effectiveInitialCategoryId], ([tree, initId]) => {
  const cat = (Array.isArray((tree as any)?.items) ? (tree as any).items : []).find((c: any) => Number(c?._id) === Number(initId || 0))
  if (!cat) return
  const catId = Number(cat._id)
  const parentId = cat.parent ? Number(cat.parent._id) : Number(cat.parentId ?? 0) || null
  if (parentId) { selectedRootId.value = parentId; selectedSubId.value = catId }
  else           { selectedRootId.value = catId;    selectedSubId.value = null  }
}, { immediate: true })

/* ── Unterkategorien ─────────────────────────────────── */
const {
  subcategories: subCats,
  hasSubcategories: hasSubCats,
  selectedCategoryId: selectedCatId,
  products,
  suggestedPrimaryStep,
} = useEditorCatalogFlow({
  tree: categoryTree,
  bootstrap: catalogBootstrap,
  selectedRootId,
  selectedSubId,
  selectedProductId: selectedProdId,
  categoryFilter: isRegularEditorCategory,
})

function selectRoot(id: number) {
  if (selectedRootId.value === id) return
  selectedRootId.value = id; selectedSubId.value = null
  selectedProdId.value = null; selectedSizeId.value = null; selectedOptions.value = new Map()
  lastSizeTitleBeforeProductSwitch.value = null
  closeHoverPreview()
}

function scrollActiveCatIntoView() {
  nextTick(() => {
    const el = catNavRef.value?.querySelector('.puo-cat-item.active') as HTMLElement | null
    el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  })
}

watch(selectedRootId, (id) => { if (id) scrollActiveCatIntoView() }, { immediate: true })
function selectSub(id: number) {
  if (selectedSubId.value === id) return
  selectedSubId.value = id
  selectedProdId.value = null; selectedSizeId.value = null; selectedOptions.value = new Map()
  lastSizeTitleBeforeProductSwitch.value = null
  closeHoverPreview()
}

/* ── Produkte ────────────────────────────────────────── */
const productsLoading = computed(() => false)

/* ── Produkt-Details ─────────────────────────────────── */
const prodLoading = computed(() => false)
const cfg = computed(() => {
  const productId = Number(selectedProdId.value || 0)
  if (!(productId > 0)) return null
  return catalogBootstrap.value?.products?.[String(productId)]?.configurator ?? null
})
const sizeGroup     = computed(() => cfg.value?.groups?.find((g: any) => g.isSize) ?? null)
const sizeOptions   = computed(() => {
  const raw = Array.isArray((sizeGroup.value as any)?.sizeOptions) ? [ ...(sizeGroup.value as any).sizeOptions ] : []
  return raw.sort(compareSizeOptions)
})
const nonSizeGroups = computed(() => (cfg.value?.groups ?? []).filter((g: any) => !g.isSize))
const allowCustomSize = computed(() => cfg.value?.product?.hasIndividualSizes === true)
const customSizeLimits = computed(() => ({
  minW: cfg.value?.product?.minSizeX ?? undefined,
  minH: cfg.value?.product?.minSizeY ?? undefined,
  maxW: cfg.value?.product?.maxSizeX ?? undefined,
  maxH: cfg.value?.product?.maxSizeY ?? undefined,
}))

function realSizeOptionsForCurrentProduct() {
  return Array.isArray(sizeGroup.value?.sizeOptions) ? [...sizeGroup.value.sizeOptions].sort(compareSizeOptions) : []
}

function parseSizeTitle(title: string | null | undefined): { title: string; widthMm: number; heightMm: number; widthCm: number; heightCm: number } | null {
  const raw = String(title || '').trim()
  if (!raw) return null
  const match = raw.replace(/cm/ig, '').match(/(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+(?:[.,]\d+)?)/i)
  if (!match) return null
  const widthCm = Number(match[1].replace(',', '.'))
  const heightCm = Number(match[2].replace(',', '.'))
  if (!Number.isFinite(widthCm) || !Number.isFinite(heightCm) || widthCm <= 0 || heightCm <= 0) return null
  return {
    title: `${widthCm} x ${heightCm} cm`,
    widthMm: Math.round(widthCm * 10),
    heightMm: Math.round(heightCm * 10),
    widthCm,
    heightCm,
  }
}

const customSizeErrors = computed(() => {
  const out: string[] = []
  if (selectedSizeId.value !== '__custom__') return out
  const width = Number(customWidthCm.value || 0)
  const height = Number(customHeightCm.value || 0)
  const { minW, minH, maxW, maxH } = customSizeLimits.value
  if (!(width > 0)) out.push('Breite eingeben')
  if (!(height > 0)) out.push('Höhe eingeben')
  if (minW != null && width > 0 && width < Number(minW)) out.push(`Breite mindestens ${minW} cm`)
  if (minH != null && height > 0 && height < Number(minH)) out.push(`Höhe mindestens ${minH} cm`)
  if (maxW != null && width > 0 && width > Number(maxW)) out.push(`Breite maximal ${maxW} cm`)
  if (maxH != null && height > 0 && height > Number(maxH)) out.push(`Höhe maximal ${maxH} cm`)
  return out
})
const customSizeValid = computed(() => customSizeErrors.value.length === 0)

/* ── Auto-Selects ───────────────────────────────────── */
// Kein globales Auto-Select für Produkte – nur übernehmen, wenn ein
// konkretes Startprodukt aus Entry/Drawer vorgegeben wurde.
watch(
  () => props.initialProductId,
  (initialProductId) => {
    const pid = Number(initialProductId || 0)
    if (!(pid > 0)) return
    if (Number(selectedProdId.value || 0) === pid) return
    selectedProdId.value = pid
  },
  { immediate: true },
)

watch(
  [products, () => props.initialProductId, selectedCatId],
  ([list, initialProductId]) => {
    const pid = Number(initialProductId || 0)
    if (!(pid > 0)) return
    if (selectedProdId.value && Number(selectedProdId.value) === pid) return
    const match = (Array.isArray(list) ? list : []).find((p: any) => Number(p?._id) === pid)
    if (!match) return
    selectedProdId.value = pid
  },
  { immediate: true },
)

watch(sizeOptions,   (opts) => {
  if (!selectedProdId.value) return
  if (selectedSizeId.value === '__custom__') return
  const d = opts.find((o: any) => o.isDefault) ?? opts[0]
  if (!selectedSizeId.value && d) selectedSizeId.value = String(d.variantId ?? d.id ?? '')
}, { immediate: true })
watch(nonSizeGroups, (grps) => { if (!selectedProdId.value) return; for (const g of grps as any[]) { const d = (g.options??[]).find((o: any)=>o.isDefault)??(g.options??[])[0]; if (d && !selectedOptions.value.has(String(g.id))) selectedOptions.value.set(String(g.id),{variantId:String(d.id??''),value:String(d.title??'')}) } }, { immediate: true })

watch(selectedProdId, async () => {
  const preferredTitle = lastSizeTitleBeforeProductSwitch.value || selectedSizeTitle()
  const previousOption = realSizeOptionsForCurrentProduct().find((o: any) => String(o?.title || '') === String(preferredTitle || '')) || null
  selectedSizeId.value = null
  customWidthCm.value = null
  customHeightCm.value = null
  showAllSizes.value = false
  if (selectedProdId.value) openAc.value = null
  closeHoverPreview()
  await nextTick()
  if (!selectedProdId.value) return
  const targetSizeOptions = realSizeOptionsForCurrentProduct()
  // Titel exakt → Titel normalisiert → gleiche Maße (auch gedreht) → Produkt-Default.
  const matched = findMatchingSizeOption(targetSizeOptions as any[], previousOption || (preferredTitle ? { title: preferredTitle } : null))
  const fallback = matched || defaultSizeOption(targetSizeOptions as any[])
  selectedSizeId.value = fallback ? String(fallback.variantId ?? fallback.id ?? '') : null
  // Die ursprüngliche Wahl behalten, wenn nur ein Fallback greift: so findet
  // ein Wechsel zurück (A → B → A) wieder die eigentlich gewünschte Größe.
  if (matched) lastSizeTitleBeforeProductSwitch.value = String(matched.title || '') || null
})

watch(allowCustomSize, (v) => {
  if (!v) {
    customSizeEnabled.value = false
    if (selectedSizeId.value === '__custom__') selectedSizeId.value = null
  }
})

watch(selectedSizeId, (val) => {
  customSizeEnabled.value = val === '__custom__'
  if (!val || val === '__custom__') return
  const opt = (sizeOptions.value as any[]).find((o: any) => String(o.variantId ?? o.id ?? '') === val)
  const parsed = parseSizeTitle(String(opt?.title || ''))
  if (parsed) {
    customWidthCm.value = parsed.widthCm
    customHeightCm.value = parsed.heightCm
  }
})

watch([customWidthCm, customHeightCm], ([w, h]) => {
  const sw = Number(w)
  const sh = Number(h)
  const snap = (v: number) => Math.round(v * 10) / 10
  if (Number.isFinite(sw) && Math.abs(sw - snap(sw)) > 0.0001) customWidthCm.value = snap(sw)
  if (Number.isFinite(sh) && Math.abs(sh - snap(sh)) > 0.0001) customHeightCm.value = snap(sh)
})

function toggleOption(groupId: string, variantId: string, value: string) {
  selectedOptions.value.set(groupId, { variantId, value })
  selectedOptions.value = new Map(selectedOptions.value)
}
function isOptSelected(groupId: string, variantId: string) {
  return selectedOptions.value.get(groupId)?.variantId === variantId
}

function toggleCustomSizeEnabled() {
  if (!allowCustomSize.value) return
  customSizeEnabled.value = !customSizeEnabled.value
  if (customSizeEnabled.value) {
    selectedSizeId.value = '__custom__'
    const parsed = parseSizeTitle(selectedSizeTitle() || '')
    if (parsed) {
      customWidthCm.value = parsed.widthCm
      customHeightCm.value = parsed.heightCm
    }
    return
  }
  const fallback = (sizeOptions.value as any[]).find((o: any) => !!o?.isDefault) || (sizeOptions.value as any[])[0] || null
  selectedSizeId.value = fallback ? String(fallback.variantId ?? fallback.id ?? '') : null
}

/* ── Accordion ───────────────────────────────────────── */
const openAc = ref<string | null>(null)
function toggleAc(id: string) { openAc.value = openAc.value === id ? null : id }

watch(selectedRootId, async (id) => {
  if (!id) {
    openAc.value = null
    return
  }
  await nextTick()
  if (selectedProdId.value) {
    openAc.value = null
    return
  }
  openAc.value = suggestedPrimaryStep.value
})

watch(hasSubCats, (has) => {
  if (!selectedRootId.value) return
  if (selectedSubId.value) return
  if (selectedProdId.value) {
    openAc.value = null
    return
  }
  openAc.value = has ? 'sub' : 'prod'
})

watch(selectedSubId, async (id) => {
  if (!id) return
  await nextTick()
  if (selectedProdId.value) {
    openAc.value = null
    return
  }
  openAc.value = 'prod'
})

watch(products, (list) => {
  if (!selectedCatId.value) return
  if (hasSubCats.value && !selectedSubId.value) return
  if (!selectedProdId.value && Array.isArray(list) && list.length) {
    openAc.value = 'prod'
  }
})

watch(openAc, () => closeHoverPreview())

/* ── Show all sizes ──────────────────────────────────── */
const showAllSizes = ref(false)
const SIZES_PREVIEW = 6
const PREVIEW_RENDER_LIMIT = 6
const visibleSizeOptions = computed(() =>
  showAllSizes.value ? sizeOptions.value : sizeOptions.value.slice(0, SIZES_PREVIEW)
)
const visiblePreviewFiles = computed(() => props.files.slice(0, PREVIEW_RENDER_LIMIT))
const hiddenPreviewCount = computed(() => Math.max(0, props.files.length - visiblePreviewFiles.value.length))
const uploadStatusLabel = computed(() => {
  const total = Number(props.uploadTotal || 0)
  const done = Number(props.uploadDone || 0)
  if (!(total > 0)) return 'Upload noch nicht gestartet'
  if (done >= total) return 'Upload abgeschlossen'
  return `${done} / ${total} hochgeladen`
})

/* ── Apply ───────────────────────────────────────────── */
const canApply = computed(() =>
  !!selectedProdId.value &&
  !!selectedCatId.value &&
  !!selectedSizeId.value &&
  !(hasSubCats.value && !selectedSubId.value) &&
  (selectedSizeId.value !== '__custom__' || customSizeValid.value)
)

const selectedProd = computed(() => (products.value as any[]).find((p: any) => Number(p._id) === selectedProdId.value) ?? null)
const selectedCat  = computed(() => {
  const id = selectedCatId.value; if (!id) return null
  return (subCats.value as any[]).find((c: any) => Number(c._id) === id)
    ?? (allRootCats.value as any[]).find((c: any) => Number(c._id) === id)
    ?? null
})
const selectedRoot = computed(() => (allRootCats.value as any[]).find((c: any) => Number(c._id) === selectedRootId.value) ?? null)
const selectedSub  = computed(() => (subCats.value as any[]).find((c: any) => Number(c._id) === selectedSubId.value) ?? null)
const selectedSubThumb = computed(() => selectedSub.value ? thumb(selectedSub.value) : '')
const selectedProdThumb = computed(() => selectedProd.value ? thumb(selectedProd.value) : '')
const pricingCfg = computed(() => {
  if (!cfg.value?.product) return null
  const groups = (cfg.value?.groups ?? []).map((group: any) => {
    if (group?.isSize) return group
    const selected = selectedOptions.value.get(String(group.id))
    const selectedOption = (group?.options ?? []).find((opt: any) => String(opt?.id ?? '') === String(selected?.variantId || '')) || null
    return {
      ...group,
      selected: selectedOption,
    }
  })
  return {
    product: cfg.value.product,
    groups,
  }
})
const selectedSizeOptionForPrice = computed(() => {
  if (selectedSizeId.value === '__custom__') {
    const parsed = parseSizeTitle(`${Number(customWidthCm.value || 0)} x ${Number(customHeightCm.value || 0)} cm`)
    if (!parsed) return null
    return {
      id: '__custom__',
      title: parsed.title,
      widthMm: parsed.widthMm,
      heightMm: parsed.heightMm,
      price: 0,
    }
  }
  if (!selectedSizeId.value) return null
  const opt = (sizeOptions.value as any[]).find((o: any) => String(o.variantId ?? o.id ?? '') === selectedSizeId.value)
  if (!opt) return null
  const parsed = parseSizeTitle(String(opt?.title || ''))
  return {
    id: String(opt.variantId ?? opt.id ?? ''),
    title: String(opt.title || ''),
    widthMm: opt?.widthMm != null ? Number(opt.widthMm) : (parsed?.widthMm ?? undefined),
    heightMm: opt?.heightMm != null ? Number(opt.heightMm) : (parsed?.heightMm ?? undefined),
    price: Number(opt?.priceSpecial ?? opt?.price ?? 0),
  }
})
const { price: livePrice, loading: livePriceLoading, fmtCurrency } = usePriceCalculator({
  cfg: () => pricingCfg.value as any,
  quantity: () => Math.max(1, Number(quantity.value || 1)),
  sizeOption: () => selectedSizeOptionForPrice.value as any,
  categoryId: () => selectedCatId.value ?? null,
})
const customSizePriceLabel = computed(() => {
  if (selectedSizeId.value !== '__custom__') return ''
  if (!customWidthCm.value || !customHeightCm.value) return ''
  if (!customSizeValid.value) return ''
  if (livePriceLoading.value) return 'Preis wird berechnet…'
  if (!(Number(livePrice.value || 0) > 0)) return 'Preis aktuell nicht verfügbar'
  return fmtCurrency ? fmtCurrency(Number(livePrice.value || 0)) : formatMoneyLike(Number(livePrice.value || 0), 'EUR')
})

function doApply() {
  if (!canApply.value) return
  const sel = selectedSizeId.value
  const sizeOpt = sel && sel !== '__custom__'
    ? (sizeOptions.value as any[]).find((o: any) => String(o.variantId ?? o.id ?? '') === sel)
    : null
  const customSize = sel === '__custom__'
    ? parseSizeTitle(`${Number(customWidthCm.value || 0)} x ${Number(customHeightCm.value || 0)} cm`)
    : null
  const catThumbSrc = selectedCat.value?.thumbnailWebp || selectedCat.value?.thumbnail || ''
  const catThumbUrl = catThumbSrc ? (catThumbSrc.startsWith('http') ? catThumbSrc : `https://www.pixelfoto-express.de${catThumbSrc}`) : undefined
  emit('apply', {
    productId: selectedProdId.value!, productTitle: selectedProd.value ? String(selectedProd.value.title ?? '') : undefined,
    categoryId: selectedCatId.value!, categoryTitle: selectedCat.value ? String(selectedCat.value.title ?? '') : undefined,
    categoryThumbnail: catThumbUrl,
    sizeVariantId: sizeOpt ? String(sizeOpt.variantId ?? sizeOpt.id ?? '') : undefined,
    sizeTitle: customSize?.title ?? (sizeOpt ? String(sizeOpt.title ?? '') : undefined),
    sizeWidthMm: customSize?.widthMm ?? (sizeOpt?.widthMm != null ? Number(sizeOpt.widthMm) : undefined),
    sizeHeightMm: customSize?.heightMm ?? (sizeOpt?.heightMm != null ? Number(sizeOpt.heightMm) : undefined),
    quantity: Math.max(1, Number(quantity.value || 1)),
    grayscale: !!grayscale.value,
    options: [...selectedOptions.value.entries()].map(([groupId, v]) => ({ groupId, variantId: v.variantId, value: v.value })),
    allGroups: cfg.value?.groups ?? [],
    tags: parsedTags.value,
  })
}

/* ── Helpers ─────────────────────────────────────────── */
function imgUrl(src?: string | null) {
  if (!src) return ''
  return src.startsWith('http') ? src : `https://www.pixelfoto-express.de${src}`
}
function thumb(item: any) {
  return imgUrl(item?.thumbnailWebp || item?.thumbnail) || ''
}
function shortTitle(s: any, max = 28) {
  const t = String(s ?? '').trim()
  return t.length > max ? t.slice(0, max - 1) + '…' : t
}

function formatMoneyLike(value: any, currency = 'EUR'): string {
  if (value == null) return ''

  if (typeof value === 'number' && Number.isFinite(value)) {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(value)
  }

  const raw = String(value || '').trim()
  if (!raw) return ''

  const symbolCurrency =
    raw.includes('€') ? 'EUR'
      : raw.includes('$') ? 'USD'
        : raw.includes('£') ? 'GBP'
          : currency

  const numeric = raw
    .replace(/[^\d,.-]/g, '')
    .replace(/\.(?=\d{3}(\D|$))/g, '')
    .replace(',', '.')

  const amount = Number(numeric)
  if (!Number.isFinite(amount)) return raw.replace('.', ',')

  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: symbolCurrency }).format(amount)
}

function sizePriceLabel(size: any): string {
  const raw = size?.priceLabel ?? size?.price ?? ''
  return formatMoneyLike(raw, String(size?.currency || 'EUR'))
}

/** Normalpreis einer Option, wenn ein Aktionspreis greift (sonst null). */
function regularPriceOf(opt: any): number | null {
  const regular = Number(opt?.regularPrice ?? 0)
  const now = Number(opt?.priceSpecial ?? opt?.price ?? 0)
  return regular > 0 && regular > now ? regular : null
}

/** Preis eines Produkts im gewaehlten Format (zeigt z. B. Matt 60x90 im Angebot, Glanz nicht). */
function productFormatPrice(p: any): { price: number; regular: number | null } | null {
  const title = selectedSizeTitle()
  if (!title || selectedSizeId.value === '__custom__') return null
  const prod: any = catalogBootstrap.value?.products?.[String(p?._id)]
  const groups: any[] = prod?.configurator?.groups ?? []
  const opt = groups.find((g: any) => g?.isSize)?.sizeOptions?.find((o: any) => o?.title === title)
  if (!opt || !(Number(opt.price) > 0)) return null
  return { price: Number(opt.price), regular: regularPriceOf(opt) }
}

function money(value: number): string {
  return formatMoneyLike(value, 'EUR')
}

function selectedSizeTitle() {
  if (selectedSizeId.value === '__custom__') {
    const parsed = parseSizeTitle(`${Number(customWidthCm.value || 0)} x ${Number(customHeightCm.value || 0)} cm`)
    return parsed?.title ?? 'Individuell'
  }
  if (!selectedSizeId.value) return null
  return (sizeOptions.value as any[]).find((o: any) => String(o.variantId ?? o.id ?? '') === selectedSizeId.value)?.title ?? null
}

const hoverPreview = ref<{
  title: string
  description: string
  image: string
  x: number
  y: number
} | null>(null)

function previewDescription(item: any): string {
  return String(item?.shortDescription || item?.description || '').trim()
}

function openHoverPreview(event: MouseEvent, item: any) {
  const target = event.currentTarget as HTMLElement | null
  if (!target) return
  const rect = target.getBoundingClientRect()
  hoverPreview.value = {
    title: String(item?.titleShort || item?.title || '').trim(),
    description: previewDescription(item),
    image: thumb(item),
    x: rect.right + 14,
    y: rect.top + Math.min(28, Math.max(6, rect.height * 0.2)),
  }
}

function closeHoverPreview() {
  hoverPreview.value = null
}

function selectOverlaySize(option: any) {
  selectedSizeId.value = String(option?.variantId ?? option?.id ?? '')
  showAllSizes.value = false
  openAc.value = null
}
</script>

<template>
  <Teleport to="body">
    <div class="puo-backdrop" @click.self="$emit('cancel')">
      <div class="puo-panel" role="dialog" aria-modal="true" aria-label="Bilder konfigurieren">

        <!-- Header -->
        <div class="puo-head">
          <div class="puo-head__copy">
            <strong class="puo-title">Bilder konfigurieren</strong>
            <div class="puo-head__meta">
              <span class="puo-count">{{ files.length }} Bild{{ files.length !== 1 ? 'er' : '' }}</span>
              <span class="puo-status">{{ uploadStatusLabel }}</span>
            </div>
          </div>
          <button class="puo-close" type="button" @click="$emit('cancel')" aria-label="Schließen">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>
        <div v-if="(uploadTotal || 0) > 0" class="puo-upload-progress" aria-hidden="true">
          <span class="puo-upload-progress__fill" :style="{ width: `${Math.max(0, Math.min(100, Number(uploadProgress || 0)))}%` }" />
        </div>

        <!-- 3-column body -->
        <div class="puo-cols">

          <!-- Left: category list -->
          <nav ref="catNavRef" class="puo-cats" aria-label="Kategorien">
            <button
              v-for="cat in filteredCats"
              :key="cat._id"
              class="puo-cat-item"
              :class="{ active: Number(cat._id) === selectedRootId }"
              type="button"
              @click="selectRoot(Number(cat._id))"
              @mouseenter="openHoverPreview($event, cat)"
              @mouseleave="closeHoverPreview()"
            >
              <div class="puo-cat-thumb-wrap">
                <img v-if="thumb(cat)" class="puo-cat-thumb" :src="thumb(cat)" :alt="cat.title" loading="lazy" />
                <div v-else class="puo-cat-thumb puo-cat-thumb--empty"></div>
              </div>
              <span class="puo-cat-label">{{ shortTitle(cat.title, 18) }}</span>
            </button>
          </nav>

          <!-- Center: configurator -->
          <div class="puo-config">

            <!-- Subcategories -->
            <div v-if="selectedRootId && hasSubCats" class="ac-item ac-item--primary">
              <button class="ac-head" type="button" @click="toggleAc('sub')">
                <img v-if="selectedSubThumb" class="ac-head__icon ac-head__icon--primary" :src="selectedSubThumb" :alt="selectedSub?.title || ''" />
                <span class="ac-head__title">Kategorie<span class="ac-head__val">{{ selectedSub ? shortTitle(selectedSub.title) : 'wählen…' }}</span></span>
                <svg class="ac-head__chev" :class="{ open: openAc === 'sub' }" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>
              </button>
              <div v-if="openAc === 'sub'" class="ac-body">
                <button
                  v-for="sub in subCats" :key="sub._id"
                  class="ac-opt" :class="{ active: Number(sub._id) === selectedSubId }"
                  type="button"
                  @click="selectSub(Number(sub._id)); openAc = 'prod'"
                  @mouseenter="openHoverPreview($event, sub)"
                  @mouseleave="closeHoverPreview()"
                >
                  <img v-if="thumb(sub)" class="ac-opt__thumb" :src="thumb(sub)" :alt="sub.title" />
                  <span>{{ sub.title }}</span>
                </button>
              </div>
            </div>

            <!-- Products -->
            <div v-if="selectedCatId && (!hasSubCats || selectedSubId)" class="ac-item ac-item--primary">
              <button class="ac-head" type="button" @click="toggleAc('prod')">
                <img v-if="selectedProdThumb" class="ac-head__icon ac-head__icon--primary" :src="selectedProdThumb" :alt="selectedProd?.title || ''" />
                <span class="ac-head__title">Produkte<span class="ac-head__val"><span v-if="productsLoading" class="ac-head__loading">Lädt…</span><span v-else-if="selectedProd">{{ shortTitle(selectedProd.title) }}</span><span v-else>wählen…</span></span></span>
                <svg class="ac-head__chev" :class="{ open: openAc === 'prod' }" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>
              </button>
              <div v-if="openAc === 'prod'" class="ac-body">
                <div v-if="productsLoading" class="ac-hint">Lädt…</div>
                <div v-else-if="!products.length" class="ac-hint">Keine Produkte gefunden.</div>
                <button
                  v-for="p in products" :key="p._id"
                  class="ac-opt" :class="{ active: Number(p._id) === selectedProdId }"
                  type="button"
                  @click="lastSizeTitleBeforeProductSwitch = selectedSizeTitle(); selectedProdId = Number(p._id); closeHoverPreview(); openAc = null"
                  @mouseenter="openHoverPreview($event, p)"
                  @mouseleave="closeHoverPreview()"
                >
                  <img v-if="thumb(p)" class="ac-opt__thumb" :src="thumb(p)" :alt="p.title" />
                  <span>{{ p.title }}</span>
                  <span v-if="productFormatPrice(p)" class="ac-opt__price" :class="{ 'is-sale': productFormatPrice(p)?.regular != null }">
                    <span v-if="productFormatPrice(p)?.regular != null" class="sale-badge">Aktion</span>
                    <s v-if="productFormatPrice(p)?.regular != null" class="fmt-row__regular">{{ money(productFormatPrice(p)?.regular ?? 0) }}</s>
                    {{ money(productFormatPrice(p)?.price ?? 0) }}
                  </span>
                </button>
              </div>
            </div>

            <!-- Format / Size (inline list) -->
            <div v-if="selectedProdId && (sizeOptions.length || allowCustomSize || prodLoading)" class="ac-item fmt-section">
              <button class="ac-head" type="button" @click="toggleAc('__size__')">
                <img v-if="(sizeGroup as any)?.icon" class="ac-head__icon" :src="imgUrl((sizeGroup as any).icon)" alt="" />
                <span class="ac-head__title">{{ sizeGroup?.title || 'Format' }}<span v-if="selectedSizeTitle()" class="ac-head__val">{{ selectedSizeTitle() }}</span></span>
                <svg class="ac-head__chev" :class="{ open: openAc === '__size__' }" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>
              </button>
              <div v-if="openAc === '__size__' && prodLoading" class="ac-hint">Lädt…</div>
              <template v-else-if="openAc === '__size__'">
                <div class="fmt-list">
                  <button
                    v-for="s in visibleSizeOptions"
                    :key="s.variantId ?? s.id"
                    class="fmt-row"
                    :class="{ active: selectedSizeId === String(s.variantId ?? s.id ?? '') }"
                    type="button"
                    @click="selectOverlaySize(s)"
                  >
                    <span class="fmt-row__title">
                      {{ s.title }}
                      <span v-if="regularPriceOf(s) !== null" class="sale-badge">Aktion</span>
                    </span>
                    <span v-if="sizePriceLabel(s)" class="fmt-row__price" :class="{ 'is-sale': regularPriceOf(s) !== null }">
                      <s v-if="regularPriceOf(s) !== null" class="fmt-row__regular">{{ money(regularPriceOf(s) ?? 0) }}</s>
                      {{ sizePriceLabel(s) }}
                    </span>
                  </button>
                </div>
                <button
                  v-if="sizeOptions.length > SIZES_PREVIEW && !showAllSizes"
                  class="fmt-show-all"
                  type="button"
                  @click="showAllSizes = true"
                >Alle anzeigen →</button>
                <div v-if="allowCustomSize" class="fmt-custom">
                  <button
                    class="fmt-custom__toggle"
                    :class="{ active: customSizeEnabled }"
                    type="button"
                    @click="toggleCustomSizeEnabled"
                  >
                    <span class="fmt-custom__toggle-copy">
                      <span class="fmt-custom__toggle-title">Individuelle Größe</span>
                      <span class="fmt-custom__toggle-sub">Eigene Breite und Höhe in 0,1-cm-Schritten eingeben.</span>
                    </span>
                    <span class="fmt-custom__toggle-switch" :class="{ on: customSizeEnabled }" aria-hidden="true">
                      <span class="fmt-custom__toggle-knob" />
                    </span>
                  </button>
                  <div v-if="customSizeEnabled" class="fmt-custom__panel">
                  <div class="fmt-custom__sub">Breite ist waagerecht, Höhe senkrecht.</div>
                  <div class="fmt-custom__grid">
                    <input
                      v-model.number="customWidthCm"
                      class="fmt-custom__input"
                      type="number"
                      min="0"
                      step="0.1"
                      placeholder="Breite in cm"
                      @focus="selectedSizeId = '__custom__'; customSizeEnabled = true"
                    />
                    <input
                      v-model.number="customHeightCm"
                      class="fmt-custom__input"
                      type="number"
                      min="0"
                      step="0.1"
                      placeholder="Höhe in cm"
                      @focus="selectedSizeId = '__custom__'; customSizeEnabled = true"
                    />
                  </div>
                  <div v-if="selectedSizeId === '__custom__' && customWidthCm && customHeightCm" class="fmt-custom__preview">
                    <span>{{ customWidthCm }} × {{ customHeightCm }} cm</span>
                    <strong v-if="customSizePriceLabel" class="fmt-custom__price" :data-loading="livePriceLoading ? '1' : '0'">{{ customSizePriceLabel }}</strong>
                  </div>
                  <div
                    v-if="customSizeLimits.minW != null || customSizeLimits.minH != null || customSizeLimits.maxW != null || customSizeLimits.maxH != null"
                    class="fmt-custom__limits"
                  >
                    <span v-if="customSizeLimits.minW != null || customSizeLimits.minH != null">
                      Min: {{ customSizeLimits.minW ?? '—' }} × {{ customSizeLimits.minH ?? '—' }} cm
                    </span>
                    <span v-if="customSizeLimits.maxW != null || customSizeLimits.maxH != null">
                      Max: {{ customSizeLimits.maxW ?? '—' }} × {{ customSizeLimits.maxH ?? '—' }} cm
                    </span>
                  </div>
                  <div v-if="selectedSizeId === '__custom__' && customSizeErrors.length" class="fmt-custom__errors">
                    <span v-for="err in customSizeErrors" :key="err">{{ err }}</span>
                  </div>
                  </div>
                </div>
              </template>
            </div>

            <!-- Other groups (accordion) — only after product selected -->
            <template v-if="selectedProdId">
              <div v-for="grp in nonSizeGroups" :key="grp.id" class="ac-item">
                <button class="ac-head" type="button" @click="toggleAc(String(grp.id))">
                  <img v-if="(grp as any).icon" class="ac-head__icon" :src="imgUrl((grp as any).icon)" alt="" />
                  <span class="ac-head__title">{{ grp.title }}<span class="ac-head__val">{{ selectedOptions.get(String(grp.id))?.value || '—' }}</span></span>
                  <svg class="ac-head__chev" :class="{ open: openAc === String(grp.id) }" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>
                </button>
                <div v-if="openAc === String(grp.id)" class="ac-body">
                  <button
                    v-for="opt in (grp as any).options" :key="opt.id"
                    class="ac-opt" :class="{ active: isOptSelected(String(grp.id), String(opt.id)) }"
                    type="button"
                    @click="toggleOption(String(grp.id), String(opt.id), String(opt.title ?? '')); openAc = null"
                  >
                    <img v-if="(opt as any).icon" class="ac-opt__thumb" :src="imgUrl((opt as any).icon)" :alt="String(opt.title)" />
                    <span>{{ opt.title }}</span>
                  </button>
                </div>
              </div>

              <div class="ac-item">
                <button class="ac-head" type="button" @click="toggleAc('settings')">
                  <span class="ac-head__title">
                    Bildstil &amp; Anzahl
                    <span class="ac-head__val">{{ grayscale ? 'S/W' : 'Farbe' }} · {{ quantity }}× pro Bild</span>
                  </span>
                  <svg class="ac-head__chev" :class="{ open: openAc === 'settings' }" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>
                </button>
                <div v-if="openAc === 'settings'" class="ac-body">
                  <div class="puo-settings">
                    <label class="puo-settings__field">
                      <span class="puo-settings__label">Anzahl pro Bild</span>
                      <div class="puo-settings__qty">
                        <button type="button" class="puo-settings__step" @click="quantity = Math.max(1, Number(quantity || 1) - 1)">−</button>
                        <input v-model.number="quantity" class="puo-settings__input" type="number" min="1" step="1" inputmode="numeric" />
                        <button type="button" class="puo-settings__step" @click="quantity = Math.max(1, Number(quantity || 1) + 1)">+</button>
                      </div>
                    </label>
                    <label class="puo-settings__field puo-settings__field--tags">
                      <span class="puo-settings__label">Tag / Album für diese Bilder</span>
                      <input v-model="tagsInput" class="puo-settings__input puo-settings__input--text" type="text" list="puo-tag-suggestions" placeholder="z. B. Kindergarten, Hochzeit" autocomplete="off" />
                      <datalist id="puo-tag-suggestions"><option v-for="t in knownTags ?? []" :key="t" :value="t" /></datalist>
                      <span class="puo-settings__help">Mehrere Tags mit Komma trennen. Später in der Bildübersicht nach Tag filtern, z. B. um erst den Kindergarten und dann die Hochzeit zu bearbeiten.</span>
                      <span v-if="tagSuggestions.length" class="puo-settings__chips">
                        <button v-for="t in tagSuggestions" :key="t" type="button" class="puo-settings__chip" @click="addSuggestedTag(t)">+ {{ t }}</button>
                      </span>
                    </label>
                    <label class="puo-settings__toggle">
                      <input v-model="grayscale" type="checkbox" class="puo-settings__toggle-input" />
                      <span class="puo-settings__switch" :class="{ 'is-on': grayscale }" aria-hidden="true">
                        <span class="puo-settings__switch-knob" />
                      </span>
                      <span class="puo-settings__toggle-copy">
                        <strong>Schwarzweiß</strong>
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </template>

            <!-- Step hints -->
            <div v-if="!selectedRootId" class="puo-gate">
              <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
              <span>Kategorie wählen</span>
            </div>
            <div v-else-if="hasSubCats && !selectedSubId" class="puo-gate">
              <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg>
              <span>Kategorie wählen</span>
            </div>
            <div v-else-if="!selectedProdId && !productsLoading" class="puo-gate">
              <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg>
              <span>Produkt wählen</span>
            </div>

          </div>

          <!-- Right: image previews -->
          <div class="puo-previews">
            <div class="puo-previews__title">Bildvorschau</div>
            <div
              v-for="(f, i) in visiblePreviewFiles"
              :key="f.id ?? i"
              class="puo-preview-item"
            >
              <div class="puo-preview-thumb">
                <img v-if="f.previewUrl" :src="f.previewUrl" :alt="f.fileName" :class="{ 'is-grayscale': grayscale }" />
                <div v-else class="puo-preview-thumb__empty"></div>
              </div>
              <span class="puo-preview-name">{{ shortTitle(f.fileName, 22) }}</span>
            </div>
            <div v-if="hiddenPreviewCount > 0" class="puo-preview-item puo-preview-item--more">
              <div class="puo-preview-thumb puo-preview-thumb--more">
                <strong>+{{ hiddenPreviewCount }}</strong>
              </div>
              <span class="puo-preview-name">weitere Bilder</span>
            </div>
          </div>

        </div>

        <!-- Footer -->
        <div class="puo-footer">
          <button class="puo-btn" type="button" @click="$emit('cancel')">Abbrechen</button>
          <button class="puo-btn puo-btn--primary" type="button" :disabled="!canApply" @click="doApply">
            Auswahl übernehmen
          </button>
        </div>

      </div>

      <div
        v-if="hoverPreview"
        class="puo-hover-card"
        :style="{ left: `${hoverPreview.x}px`, top: `${hoverPreview.y}px` }"
      >
        <img
          v-if="hoverPreview.image"
          class="puo-hover-card__img"
          :src="hoverPreview.image"
          :alt="hoverPreview.title"
          loading="lazy"
          decoding="async"
        />
        <div class="puo-hover-card__body">
          <strong class="puo-hover-card__title">{{ hoverPreview.title }}</strong>
          <p v-if="hoverPreview.description" class="puo-hover-card__desc">{{ hoverPreview.description }}</p>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
/* ── Backdrop & Panel ────────────────────────────────── */
.puo-backdrop {
  position: fixed; inset: 0; z-index: 10000;
  background: rgba(0,0,0,.5);
  display: flex; align-items: center; justify-content: center;
  padding: 16px;
}
.puo-panel {
  background: #fff;
  width: min(1440px, calc(100vw - 32px));
  max-width: 1440px;
  height: min(94vh, 860px);
  display: flex; flex-direction: column;
  border-radius: 10px; overflow: hidden;
  box-shadow: 0 24px 72px rgba(0,0,0,.28);
}

/* ── Header ──────────────────────────────────────────── */
.puo-head {
  display: flex; align-items: center; gap: 8px;
  padding: 12px 16px; border-bottom: 1px solid #ececec; flex: 0 0 auto;
}
.puo-head__copy{
  margin-right: auto;
  min-width: 0;
  display: grid;
  gap: 6px;
}
.puo-head__meta{
  display:flex;
  align-items:center;
  gap:8px;
  flex-wrap:wrap;
}
.puo-title { font-size: 14px; font-weight: 700; color: #141414; margin-right: auto; }
.puo-count {
  font-size: 11px; font-weight: 600; background: #f0f0f0; color: #555;
  padding: 2px 8px; border-radius: 999px;
}
.puo-status{
  font-size: 11px;
  font-weight: 600;
  color: #8a6f2d;
  background: rgba(210, 184, 121, .18);
  padding: 2px 8px;
  border-radius: 999px;
}
.puo-close {
  width: 28px; height: 28px; border: 1px solid #e0e0e0; border-radius: 5px;
  background: transparent; cursor: pointer;
  display: flex; align-items: center; justify-content: center; color: #666;
  &:hover { background: #f5f5f5; }
}
.puo-upload-progress{
  height: 6px;
  background: #f1f2f5;
  overflow: hidden;
  flex: 0 0 auto;
}
.puo-upload-progress__fill{
  display:block;
  height:100%;
  width:0;
  background: linear-gradient(90deg, #d2b879 0%, #e7d6a7 100%);
}

.puo-settings{
  display:grid;
  gap:12px;
  padding:14px 18px;
  background:#fff;
}
.puo-settings__field{
  display:grid;
  gap:6px;
}
.puo-settings__label{
  font-size:12px;
  font-weight:700;
  color:#5f4a1f;
}
.puo-settings__qty{
  display:grid;
  grid-template-columns:42px minmax(0,1fr) 42px;
  border:1px solid #d9d0be;
  background:#fff;
}
.puo-settings__step{
  border:0;
  background:#fff;
  font-size:18px;
  color:#1f2328;
  cursor:pointer;
}
.puo-settings__step + .puo-settings__input,
.puo-settings__input + .puo-settings__step{
  border-left:1px solid #d9d0be;
}
.puo-settings__input{
  width:100%;
  min-width:0;
  border:0;
  background:#fff;
  text-align:center;
  height:42px;
  font-size:14px;
  font-weight:700;
  color:#1f2328;
  appearance:textfield;
  -moz-appearance:textfield;
}
.puo-settings__input::-webkit-outer-spin-button,
.puo-settings__input::-webkit-inner-spin-button{
  -webkit-appearance:none;
  margin:0;
}
.puo-settings__toggle{
  display:flex;
  align-items:center;
  gap:10px;
  cursor:pointer;
  padding:4px 0;
}
.puo-settings__toggle-input{
  position:absolute;
  opacity:0;
  pointer-events:none;
}
.puo-settings__switch{
  width:38px;
  height:22px;
  border-radius:999px;
  background:#d1d5db;
  position:relative;
  flex:0 0 auto;
  transition:background .18s ease;
}
.puo-settings__switch.is-on{
  background:#111827;
}
.puo-settings__switch-knob{
  position:absolute;
  top:2px;
  left:2px;
  width:18px;
  height:18px;
  border-radius:999px;
  background:#fff;
  box-shadow:0 1px 2px rgba(16,24,40,.15);
  transition:transform .18s ease;
}
.puo-settings__switch.is-on .puo-settings__switch-knob{
  transform:translateX(16px);
}
.puo-settings__toggle-copy{
  display:flex;
  align-items:center;
}
.puo-settings__toggle-copy strong{
  font-size:13px;
  color:#1f2328;
}

/* ── 3-col layout ────────────────────────────────────── */
.puo-cols {
  flex: 1 1 auto; min-height: 0;
  display: grid;
  grid-template-columns: minmax(190px, 18%) minmax(420px, 34%) minmax(0, 1fr);
}

/* ── Left: categories ────────────────────────────────── */
.puo-cats {
  border-right: 1px solid #ececec;
  overflow-y: auto; overflow-x: hidden;
  display: flex; flex-direction: column; gap: 2px;
  padding: 10px 8px;
  scrollbar-width: thin; scrollbar-color: rgba(15,23,42,.1) transparent;
  background: #fafafa;
}
.puo-cat-item {
  display: flex; flex-direction: column; align-items: center;
  gap: 8px; padding: 10px 12px;
  border: 1px solid transparent; border-radius: 5px;
  background: transparent; cursor: pointer; width: 100%;
  transition: background 100ms, border-color 100ms;
  &:hover { background: rgba(15,23,42,.05); }
  &.active {
    background: #fff;
    border-color: #e30b17;
    box-shadow: 0 1px 4px rgba(227,11,23,.12);
  }
}
.puo-cat-thumb-wrap { position: relative; width: 100%; }
.puo-cat-thumb {
  display: block; width: 100%; aspect-ratio: 3/2;
  object-fit: cover; border-radius: 4px;
  background: rgba(15,23,42,.05);
  &--empty { background: rgba(15,23,42,.06); }
}
.puo-cat-label {
  font-size: 14px; line-height: 1.3; text-align: center;
  color: rgba(15,23,42,.7); word-break: break-word; hyphens: auto;
  max-width: 148px;
}
.puo-cat-item.active .puo-cat-label { color: #0f172a; font-weight: 700; }

/* ── Center: configurator ────────────────────────────── */
.puo-config {
  overflow-y: auto; overflow-x: hidden;
  border-right: 1px solid #ececec;
  display: flex; flex-direction: column;
  scrollbar-width: thin; scrollbar-color: rgba(15,23,42,.1) transparent;
  background: #fff;
}

/* ── Accordion ───────────────────────────────────────── */
.ac-item {
  border-bottom: 1px solid rgba(15,23,42,.07);
  flex: 0 0 auto;
}
.ac-item--primary .ac-head {
  padding: 16px 18px;
}
.ac-item--primary .ac-head__title {
  font-size: 16px;
  font-weight: 700;
}
.ac-head {
  display: flex; align-items: center; gap: 8px;
  width: 100%; padding: 14px 18px;
  border: 0; background: transparent; cursor: pointer; text-align: left;
  &:hover { background: rgba(15,23,42,.03); }
}
.ac-head__icon {
  flex: 0 0 26px; width: 26px; height: 26px;
  object-fit: contain; border-radius: 3px;
}
.ac-head__icon--primary {
  flex: 0 0 42px; width: 42px; height: 42px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid rgba(15,23,42,.1);
}
.ac-head__title {
  font-size: 15px; font-weight: 600; color: #141414;
  flex: 1 1 auto; display: flex; flex-direction: column; gap: 2px;
}
.ac-head__val {
  font-size: 15px; font-weight: 500; color: rgba(15,23,42,.45);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.ac-head__loading { font-style: italic; }
.ac-head__chev {
  flex-shrink: 0; color: rgba(15,23,42,.4);
  transition: transform 150ms;
  &.open { transform: rotate(180deg); }
}
.ac-body {
  max-height: 300px; overflow-y: auto;
  background: #fafafa;
  border-top: 1px solid rgba(15,23,42,.07);
}
.ac-opt {
  display: flex; align-items: center; gap: 8px;
  width: 100%; padding: 12px 18px;
  border: 0; border-bottom: 1px solid rgba(15,23,42,.05);
  background: transparent; text-align: left; cursor: pointer;
  font-size: 15px; color: #222;
  &:last-child { border-bottom: 0; }
  &:hover { background: rgba(15,23,42,.04); }
  &.active {
    background: #fff3f3; color: #c00;
    font-weight: 600;
  }
}
.ac-opt__thumb {
  flex: 0 0 36px; width: 36px; height: 36px;
  object-fit: cover; border-radius: 3px; border: 1px solid #e8e8e8;
}
.ac-hint { font-size: 13px; color: #999; padding: 12px 16px; }

/* ── Format section ──────────────────────────────────── */
.fmt-section {
  flex: 0 0 auto;
  border-bottom: 1px solid rgba(15,23,42,.07);
  padding-bottom: 4px;
}
.fmt-head {
  width: 100%;
  border: 0;
  background: transparent;
  display: flex; align-items: baseline; gap: 8px;
  justify-content: space-between;
  padding: 12px 16px 8px;
  cursor: pointer;
  text-align: left;
}
.fmt-head__title {
  font-size: 14px; font-weight: 600; color: #141414; flex: 1;
}
.fmt-head__meta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: none;
}
.fmt-head__val {
  font-size: 13px; color: rgba(15,23,42,.5);
}
.fmt-list { padding: 0 16px 0; display: flex; flex-direction: column; gap: 4px; }
.fmt-custom-toggle {
  margin: 12px 16px 0;
  border: 1px solid rgba(15,23,42,.1);
  border-radius: 10px;
  background: #fff;
  padding: 12px 14px;
}
.fmt-custom-toggle.active {
  border-color: rgba(227,11,23,.24);
  background: rgba(227,11,23,.03);
}
.fmt-custom-toggle__label {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
}
.fmt-custom-toggle__label input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}
.fmt-custom-toggle__box {
  width: 18px;
  height: 18px;
  border: 1px solid rgba(15,23,42,.2);
  border-radius: 4px;
  background: #fff;
  position: relative;
  flex: 0 0 auto;
  margin-top: 2px;
}
.fmt-custom-toggle__label input:checked + .fmt-custom-toggle__box {
  border-color: #e30b17;
  background: #e30b17;
}
.fmt-custom-toggle__label input:checked + .fmt-custom-toggle__box::after {
  content: '';
  position: absolute;
  left: 5px;
  top: 1px;
  width: 5px;
  height: 10px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}
.fmt-custom-toggle__text {
  display: grid;
  gap: 2px;
}
.fmt-custom-toggle__text strong {
  font-size: 14px;
  font-weight: 700;
  color: #18212f;
}
.fmt-custom-toggle__text small {
  color: rgba(15,23,42,.6);
  font-size: 12px;
}
.fmt-row {
  display: flex; align-items: center; justify-content: space-between;
  padding: 9px 12px; border-radius: 4px;
  border: 1px solid transparent;
  border-left: 3px solid transparent;
  background: transparent; cursor: pointer; text-align: left;
  transition: background 80ms, border-color 80ms;
  &:hover { background: rgba(15,23,42,.04); }
  &.active {
    background: #fff8f8;
    border-color: transparent;
    border-left-color: #e30b17;
    font-weight: 600;
  }
}
.fmt-row__title { font-size: 14px; color: #222; }
.fmt-row__price { font-size: 13px; color: rgba(15,23,42,.5); white-space: nowrap; }
.fmt-row.active .fmt-row__price { color: rgba(15,23,42,.6); }
.fmt-row__price.is-sale, .fmt-row.active .fmt-row__price.is-sale { color: #e30b17; font-weight: 600; }
.fmt-row__regular { margin-right: 4px; color: rgba(15,23,42,.45); font-weight: 400; }
.sale-badge {
  display: inline-block; margin-left: 6px; padding: 1px 6px;
  border-radius: 999px; background: #e30b17; color: #fff;
  font-size: 10px; font-weight: 700; letter-spacing: .02em;
  text-transform: uppercase; vertical-align: middle;
}
.ac-opt__price {
  margin-left: auto; flex-shrink: 0;
  font-size: 13px; font-weight: 600; font-variant-numeric: tabular-nums;
  color: rgba(15,23,42,.5); white-space: nowrap;
  .sale-badge { margin: 0 6px 0 0; }
  &.is-sale { color: #e30b17; }
}
.fmt-show-all {
  border: 0;
  border-top: 1px solid rgba(15,23,42,.08);
  background: transparent;
  color: #e30b17;
  font-size: 12px;
  font-weight: 600;
  padding: 8px 16px;
  text-align: left;
  cursor: pointer;
  width: 100%;
  &:hover { text-decoration: underline; }
}
.fmt-custom {
  border-top: 1px solid rgba(15,23,42,.08);
  background: transparent;
}
.fmt-custom__toggle {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border: 0;
  background: transparent;
  cursor: pointer;
  text-align: left;
}
.fmt-custom__toggle.active {
  background: rgba(227, 11, 23, 0.04);
}
.fmt-custom__toggle-copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.fmt-custom__toggle-title {
  font-size: 13px;
  font-weight: 700;
  color: #18212f;
}
.fmt-custom__toggle-sub {
  font-size: 11px;
  color: rgba(15,23,42,.6);
}
.fmt-custom__toggle-switch {
  width: 36px;
  height: 22px;
  border-radius: 999px;
  background: #d0d5dd;
  position: relative;
  flex-shrink: 0;
  transition: background .18s ease;
}
.fmt-custom__toggle-switch.on {
  background: #111827;
}
.fmt-custom__toggle-knob {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.15);
  transition: transform .18s ease;
}
.fmt-custom__toggle-switch.on .fmt-custom__toggle-knob {
  transform: translateX(14px);
}
.fmt-custom__panel {
  padding: 0 16px 12px;
}
.fmt-custom__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  min-width: 0;
}
.fmt-custom__grid > * {
  min-width: 0;
}
.fmt-custom__input {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  height: 38px;
  padding: 0 12px;
  border: 1px solid rgba(15,23,42,.12);
  border-radius: 4px;
  background: #fff;
  font-size: 14px;
  color: #141414;
}
.fmt-custom__limits,
.fmt-custom__errors {
  display: grid;
  gap: 4px;
  margin-top: 8px;
  font-size: 12px;
}
.fmt-custom__preview {
  margin-top: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #141414;
}
.fmt-custom__price {
  color: rgba(15,23,42,.62);
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}
.fmt-custom__price[data-loading="1"] {
  color: rgba(15,23,42,.46);
}
.fmt-custom__limits { color: rgba(15,23,42,.55); }
.fmt-custom__errors { color: #c81e1e; }

/* ── Gate (no category selected) ────────────────────── */
.puo-gate {
  flex: 1 1 auto; display: flex; flex-direction: column;
  align-items: center; justify-content: center; gap: 10px;
  color: rgba(15,23,42,.3); font-size: 13px; padding: 32px;
  text-align: center;
}

/* ── Right: image previews ───────────────────────────── */
.puo-previews {
  overflow-y: auto; overflow-x: hidden;
  padding: 16px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-auto-rows: max-content;
  gap: 14px;
  align-content: start;
  background: #f4f4f5;
  scrollbar-width: thin; scrollbar-color: rgba(15,23,42,.1) transparent;
}
.puo-previews__title{
  grid-column: 1 / -1;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .02em;
  color: rgba(15,23,42,.62);
  text-transform: uppercase;
  margin: 0 0 2px;
}
.puo-preview-item {
  display: flex; flex-direction: column;
  background: #fff; border-radius: 7px;
  border: 1px solid #e4e4e7;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,.06);
}
.puo-preview-item--more {
  justify-content: center;
}
.puo-preview-thumb {
  width: 100%;
  aspect-ratio: 4 / 3;
  min-height: 110px;
  background: #f0f0f0; overflow: hidden;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    &.is-grayscale { filter: grayscale(1); }
  }
}
.puo-preview-thumb__empty {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #eef0f3, #e2e6eb);
}
.puo-preview-thumb--more {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #eceff3;
  color: #18212f;
  font-size: 28px;
  font-weight: 700;
}
.puo-preview-name {
  font-size: 12px; color: rgba(15,23,42,.6);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  padding: 7px 9px 8px;
  line-height: 1.3;
}

@media (min-width: 1320px) {
  .puo-previews {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

/* ── Footer ──────────────────────────────────────────── */
.puo-footer {
  position: relative;
  z-index: 2;
  display: flex; gap: 8px; justify-content: flex-end;
  padding: 10px 16px; border-top: 1px solid #ececec; flex: 0 0 auto;
  background: #fff;
}
.puo-btn {
  height: 34px; padding: 0 18px; border: 1px solid #ddd; border-radius: 5px;
  background: #fff; font-size: 13px; font-weight: 600; cursor: pointer;
  &:hover { background: #f5f5f5; }
  &:disabled { opacity: .45; cursor: not-allowed; }
}
.puo-btn--primary {
  background: #e30b17; color: #fff; border-color: #e30b17;
  &:hover:not(:disabled) { background: #c80913; }
}

.puo-hover-card {
  position: fixed;
  z-index: 10020;
  width: min(360px, calc(100vw - 24px));
  border-radius: 10px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.98);
  border: 1px solid #e4e4e7;
  box-shadow: 0 18px 42px rgba(15, 23, 42, 0.18);
  pointer-events: none;
  backdrop-filter: blur(8px);
}

.puo-hover-card__img {
  display: block;
  width: 100%;
  aspect-ratio: 16 / 10;
  object-fit: cover;
  background: #f4f4f5;
}

.puo-hover-card__body {
  display: grid;
  gap: 6px;
  padding: 14px 15px 15px;
}

.puo-hover-card__title {
  font-size: 15px;
  font-weight: 700;
  line-height: 1.2;
  color: #141414;
}

.puo-hover-card__desc {
  margin: 0;
  font-size: 13px;
  line-height: 1.45;
  color: rgba(15, 23, 42, 0.68);
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* ── Tablet / Mobile layout ──────────────────────────── */
@media (max-width: 960px) {
  .puo-backdrop {
    padding: 0;
    align-items: stretch;
    justify-content: stretch;
  }
  .puo-panel {
    width: 100vw;
    max-width: 100vw;
    height: 100dvh;
    max-height: 100dvh;
    border-radius: 0;
    box-shadow: none;
  }
  .puo-head {
    padding: 12px 14px;
  }
  .puo-head__copy {
    gap: 4px;
  }
  .puo-head__meta {
    gap: 6px;
  }
  .puo-cols {
    display: flex;
    flex-direction: column;
    min-height: 0;
  }
  .puo-cats {
    order: 1;
    flex: 0 0 auto;
    display: flex;
    flex-direction: row;
    gap: 8px;
    overflow-x: auto;
    overflow-y: hidden;
    padding: 10px 12px;
    border-right: 0;
    border-bottom: 1px solid #ececec;
    background: #fff;
    scroll-snap-type: x proximity;
  }
  .puo-cat-item {
    flex: 0 0 108px;
    min-width: 108px;
    padding: 8px;
    gap: 6px;
    scroll-snap-align: start;
  }
  .puo-cat-thumb {
    aspect-ratio: 1 / 1;
  }
  .puo-cat-label {
    max-width: none;
    font-size: 12px;
    line-height: 1.25;
  }
  .puo-config {
    order: 2;
    flex: 1 1 auto;
    min-height: 0;
    border-right: 0;
  }
  .ac-item--primary .ac-head {
    padding: 14px 14px;
  }
  .ac-head {
    padding: 12px 14px;
  }
  .ac-head__title,
  .ac-head__val,
  .ac-opt,
  .fmt-row__title {
    font-size: 14px;
  }
  .ac-body {
    max-height: 36vh;
  }
  .fmt-head {
    padding: 12px 14px 8px;
  }
  .fmt-list {
    padding: 0 14px;
  }
  .fmt-custom-toggle,
  .fmt-custom,
  .fmt-show-all {
    margin-left: 14px;
    margin-right: 14px;
  }
  .puo-previews {
    order: 3;
    flex: 0 0 auto;
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: minmax(112px, 132px);
    grid-template-columns: none;
    gap: 10px;
    padding: 10px 12px calc(10px + env(safe-area-inset-bottom, 0px));
    overflow-x: auto;
    overflow-y: hidden;
    border-top: 1px solid #ececec;
    background: #f7f7f8;
  }
  .puo-preview-item {
    border-radius: 6px;
  }
  .puo-preview-thumb {
    min-height: 84px;
  }
  .puo-preview-name {
    white-space: normal;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    min-height: 38px;
  }
  .puo-footer {
    padding: 10px 12px calc(10px + env(safe-area-inset-bottom, 0px));
    gap: 10px;
  }
}

/* ── Mobile fallback ─────────────────────────────────── */
@media (max-width: 640px) {
  .puo-panel {
    height: 100dvh;
  }
  .puo-head {
    padding: 10px 12px;
  }
  .puo-title {
    font-size: 13px;
  }
  .puo-count,
  .puo-status {
    font-size: 10px;
  }
  .puo-cats {
    padding: 8px 10px;
    gap: 6px;
  }
  .puo-cat-item {
    flex-basis: 92px;
    min-width: 92px;
    padding: 7px;
  }
  .puo-config {
    padding-bottom: 0;
  }
  .fmt-custom__grid {
    grid-template-columns: 1fr;
  }
  .puo-previews {
    display: none;
  }
  .puo-footer {
    justify-content: stretch;
  }
  .puo-btn {
    flex: 1 1 0;
    height: 40px;
    padding: 0 14px;
    font-size: 14px;
  }
}
.puo-settings__field--tags { display: grid; gap: 6px; }
.puo-settings__input--text { width: 100%; text-align: left; padding: 8px 10px; }
.puo-settings__help { font-size: 12px; line-height: 1.4; color: rgba(15, 23, 42, 0.6); }
.puo-settings__chips { display: flex; flex-wrap: wrap; gap: 6px; }
.puo-settings__chip { border: 1px solid rgba(15, 23, 42, 0.16); border-radius: 999px; background: #fff; padding: 3px 10px; font: inherit; font-size: 12px; cursor: pointer; }
.puo-settings__chip:hover { border-color: var(--pfe-color-accent, #e30b17); color: var(--pfe-color-accent, #e30b17); }
</style>
